import React from 'react';
import { pdf, type DocumentProps } from '@react-pdf/renderer';
import { Download } from 'lucide-react';
import { generateQrDataUrl } from '../../utils/qr';
import { downloadBlob } from '../../utils/downloadBlob';
import { cn } from '../../utils/cn';

interface DownloadLabelButtonProps {
  /** Absolute URL the QR encodes. */
  scanUrl: string;
  /** Build the react-pdf document once the QR data-URL is ready. */
  renderDocument: (qrDataUrl: string) => React.ReactElement;
  /** Download filename, without extension. */
  fileName: string;
  /** Button text. */
  label: string;
  className?: string;
  'data-test-id'?: string;
}

export const DownloadLabelButton: React.FC<DownloadLabelButtonProps> = ({
  scanUrl,
  renderDocument,
  fileName,
  label,
  className,
  'data-test-id': testId,
}) => {
  const [isGenerating, setIsGenerating] = React.useState(false);

  const handleClick = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    try {
      const qrDataUrl = await generateQrDataUrl(scanUrl);
      const blob = await pdf(renderDocument(qrDataUrl) as React.ReactElement<DocumentProps>).toBlob();
      downloadBlob(blob, `${fileName}.pdf`);
    } catch (err) {
      console.error('Failed to generate label:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isGenerating}
      data-test-id={testId}
      className={cn(
        'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed',
        className,
      )}
    >
      <Download className="h-4 w-4" />
      {isGenerating ? '…' : label}
    </button>
  );
};
